
import React from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { ModalCampaign } from "@/types/modalCampaigns";
import { User } from "@/context/AuthContext";

import { useCampaignForm } from './form/useCampaignForm';
import BasicInfoSection from './form/BasicInfoSection';
import DateSection from './form/DateSection';
import FrequencySection from './form/FrequencySection';
import UserSelection from './form/UserSelection';
import QuizToggle from './form/QuizToggle';
import QuizSettingsSection from './form/QuizSettingsSection';
import DismissToggle from './form/DismissToggle';

interface CampaignFormProps {
  currentUser: User | null;
  existingCampaign: ModalCampaign | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const CampaignForm: React.FC<CampaignFormProps> = ({ 
  currentUser, 
  existingCampaign, 
  onSuccess, 
  onCancel 
}) => {
  // Fetch assigned users for this campaign if editing
  const { data: assignedUsers = [] } = useQuery({
    queryKey: ["campaign-users", existingCampaign?.id],
    queryFn: async () => {
      if (!existingCampaign) return [];
      
      const { data, error } = await supabase
        .from("modal_campaign_users")
        .select("user_id")
        .eq("campaign_id", existingCampaign.id);
      
      if (error) throw error;
      return data.map(item => item.user_id);
    },
    enabled: !!existingCampaign
  });

  const {
    form,
    questions,
    isSubmitting,
    handleQuizToggle,
    addQuestion,
    updateQuestion,
    removeQuestion,
    handleQuestionsGenerated,
    onSubmit
  } = useCampaignForm({
    currentUser,
    existingCampaign,
    assignedUsers,
    onSuccess
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          {/* Basic Information (Title and Content) */}
          <BasicInfoSection form={form} />
          
          {/* Date Range Fields */}
          <DateSection form={form} />
          
          {/* Frequency Field */}
          <FrequencySection form={form} />
          
          {/* Users Field */}
          <UserSelection form={form} />
          
          {/* Quiz Toggle */}
          <QuizToggle form={form} onToggle={handleQuizToggle} />
          
          {/* Quiz Settings (conditional) */}
          {form.watch("has_quiz") && (
            <QuizSettingsSection
              form={form}
              questions={questions}
              onQuestionsGenerated={handleQuestionsGenerated}
              onAddQuestion={addQuestion}
              onUpdateQuestion={updateQuestion}
              onRemoveQuestion={removeQuestion}
            />
          )}
          
          {/* Allow Dismiss Toggle */}
          <DismissToggle form={form} />
        </div>
        
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuleren
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Opslaan..." : existingCampaign ? "Bijwerken" : "Aanmaken"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CampaignForm;
